import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { X, UserPlus, Loader2, UserCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { isSalesManagerRole } from "@/lib/roleAliases";

/**
 * Shared lead capture dialog. Used from both /executive/leads and /manager/leads.
 * Fetches branches + sales managers for the dependent Branch / Sales Manager picker.
 *
 * When a Sales Manager opens the dialog, they see an additional "Route to Self"
 * toggle (OPT-IN, default OFF). When enabled, the lead is created AND immediately
 * promoted into the SM's Sales Pipeline. When left OFF, the lead lands in the SM's
 * "mine" bucket in the Lead Ledger for later routing (to self or to a team member).
 */
export default function LeadCaptureDialog({ open, onClose, onCreated }) {
  const { roleName } = useAuth();
  const isSalesManager = isSalesManagerRole(roleName);
  const [branches, setBranches] = useState([]);
  const [salesManagers, setSalesManagers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [routeToSelf, setRouteToSelf] = useState(false);
  const [form, setForm] = useState({
    client_name: "", phone: "", email: "", location: "", source: "", remarks: "",
    project_name: "", branch_id: "", assigned_sales_manager_id: "", lead_date: "",
  });
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!open) return;
    setErr("");
    // Route to Self is OPT-IN — SM can log the lead first, then decide later whether
    // to keep it in her own bucket or delegate to an executive (Feb 2026 spec change).
    setRouteToSelf(false);
    setForm({
      client_name: "", phone: "", email: "", location: "", source: "", remarks: "",
      project_name: "", branch_id: "", assigned_sales_manager_id: "", lead_date: "",
    });
    api.get("/branches").then((r) => setBranches(r.data)).catch(() => {});
  }, [open, isSalesManager]);

  const loadSalesManagers = useCallback(async (branchId) => {
    try {
      const params = branchId ? `?branch_id=${encodeURIComponent(branchId)}` : "";
      const { data } = await api.get(`/users/sales-managers${params}`);
      setSalesManagers(data);
    } catch (_e) {
      setSalesManagers([]);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    loadSalesManagers(form.branch_id || "");
    // Reset SM if branch changed and current SM is not in the new branch list.
    setForm((f) => ({ ...f, assigned_sales_manager_id: "" }));
  }, [open, form.branch_id, loadSalesManagers]);

  const smOptions = useMemo(
    () => salesManagers.map((m) => ({
      id: m.id,
      label: `${m.name}${m.branch_name ? " · " + m.branch_name : " · Global"}`,
    })),
    [salesManagers]
  );

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!form.client_name.trim() || !form.phone.trim()) {
      setErr("Client name and phone are required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        client_name: form.client_name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim() || null,
        location: form.location.trim() || null,
        source: form.source.trim() || null,
        remarks: form.remarks.trim() || null,
        project_name: form.project_name.trim() || null,
        branch_id: form.branch_id || null,
        assigned_sales_manager_id: form.assigned_sales_manager_id || null,
        route_to_self: routeToSelf,
        lead_date: form.lead_date || null,
      };
      const { data } = await api.post("/leads", payload);
      toast.success(routeToSelf
        ? `Lead captured & routed to your pipeline · ${data.client_name}`
        : `Lead captured · ${data.client_name}`);
      onCreated?.(data);
      onClose();
    } catch (e2) {
      setErr(formatApiErrorDetail(e2.response?.data?.detail) || "Failed");
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose} data-testid="lead-capture-dialog">
      <div className="w-full max-w-lg iz-card p-8 relative max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 p-1.5 rounded hover:bg-[#1c1c1c] text-gray-500" data-testid="lead-capture-close">
          <X className="w-4 h-4" />
        </button>
        <div className="flex items-center gap-3 pb-4 border-b border-[#262626] mb-5">
          <UserPlus className="w-5 h-5 text-[#d4af37]" strokeWidth={1.5} />
          <div>
            <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">Capture</p>
            <h2 className="font-display text-xl text-white">New client contact</h2>
          </div>
        </div>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-2">Client Name</label>
            <input value={form.client_name} onChange={(e) => setForm(f => ({...f, client_name: e.target.value}))} className="iz-input" required data-testid="lead-name" />
          </div>
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-2">Project Name</label>
            <input value={form.project_name} onChange={(e) => setForm(f => ({...f, project_name: e.target.value}))} className="iz-input" placeholder="e.g. 2BHK Interior, Villa Renovation" data-testid="lead-project-name" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-2">Phone</label>
              <input value={form.phone} onChange={(e) => setForm(f => ({...f, phone: e.target.value}))} className="iz-input" required data-testid="lead-phone" />
            </div>
            <div>
              <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-2">Email</label>
              <input type="email" value={form.email} onChange={(e) => setForm(f => ({...f, email: e.target.value}))} className="iz-input" data-testid="lead-email" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-2">Location</label>
              <input value={form.location} onChange={(e) => setForm(f => ({...f, location: e.target.value}))} className="iz-input" data-testid="lead-location" />
            </div>
            <div>
              <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-2">Source</label>
              <input value={form.source} onChange={(e) => setForm(f => ({...f, source: e.target.value}))} className="iz-input" placeholder="Field visit, Referral…" data-testid="lead-source" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-2">Branch</label>
              <select value={form.branch_id} onChange={(e) => setForm(f => ({...f, branch_id: e.target.value}))} className="iz-input" data-testid="lead-branch">
                <option value="">— All branches —</option>
                {branches.map((b) => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-2">
                Assign Sales Manager
                {form.branch_id && <span className="text-gray-600 lowercase"> · same branch</span>}
              </label>
              <select value={form.assigned_sales_manager_id} onChange={(e) => setForm(f => ({...f, assigned_sales_manager_id: e.target.value}))} className="iz-input" data-testid="lead-sales-manager">
                <option value="">— None —</option>
                {smOptions.map((m) => (<option key={m.id} value={m.id}>{m.label}</option>))}
              </select>
              {form.branch_id && smOptions.length === 0 && (
                <p className="text-[10px] text-gray-500 mt-1">No Sales Manager assigned to this branch yet.</p>
              )}
            </div>
          </div>
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-2">Remarks</label>
            <textarea value={form.remarks} onChange={(e) => setForm(f => ({...f, remarks: e.target.value}))} className="iz-input" rows={3} data-testid="lead-remarks" />
          </div>

          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-2">
              Lead Date <span className="text-gray-600 lowercase">· optional</span>
            </label>
            <input
              type="date"
              value={form.lead_date}
              onChange={(e) => setForm(f => ({...f, lead_date: e.target.value}))}
              max={new Date().toISOString().slice(0, 10)}
              className="iz-input"
              data-testid="lead-date-input"
            />
            <p className="text-[10px] text-gray-500 mt-1 font-mono">Leave blank to use today. Backdate to file the lead into an earlier month.</p>
          </div>

          {isSalesManager && (
            <label className="flex items-start gap-3 cursor-pointer border border-[#262626] rounded-md px-4 py-3 hover:border-[#d4af37]/50" data-testid="route-to-self-container">
              <input
                type="checkbox"
                checked={routeToSelf}
                onChange={(e) => setRouteToSelf(e.target.checked)}
                className="mt-0.5 accent-[#d4af37]"
                data-testid="route-to-self-checkbox"
              />
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <UserCheck className="w-3.5 h-3.5 text-[#d4af37]" />
                  <span className="text-sm text-white">Route to Self</span>
                </div>
                <p className="text-[10px] text-gray-500 mt-1 leading-relaxed">
                  Optional. Tick to drop this lead directly into <span className="text-gray-300">my Sales Pipeline</span>. Leave unchecked to keep it in the Lead Ledger — route it to self or a team member later.
                </p>
              </div>
            </label>
          )}

          {err && <div className="text-sm text-red-400 border border-red-500/30 bg-red-500/5 rounded-md px-3 py-2">{err}</div>}
          <div className="flex justify-end gap-3 pt-4 border-t border-[#1a1a1a]">
            <button type="button" onClick={onClose} className="iz-btn-secondary" data-testid="lead-capture-cancel">Cancel</button>
            <button type="submit" disabled={saving} className="iz-btn-primary flex items-center gap-2" data-testid="lead-submit">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
              {saving ? "Saving…" : "Log Lead"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
